import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckIns, type CheckInVisibility } from '@/lib/api/v1';
import { Button, Icon, Input, Modal, Textarea } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { useToasts } from '@/lib/store';

/**
 * Drinking log modal. Reusable from any place a user wants to record a
 * tasting — bottle-detail page, scan result, catalog browse, etc. Pass
 * either `bottleId` (cellar context) or `productId` (catalog context);
 * the backend builds the snapshot from whichever is provided.
 */
interface CheckInModalProps {
  open: boolean;
  onClose: () => void;
  bottleId?: string;
  productId?: string;
  /** Title shown at the top of the modal — usually the bottle/product name. */
  bottleName: string;
  /** Optional preview image. */
  imageUrl?: string;
}

const SERVING_STYLES: { value: string; label: string }[] = [
  { value: 'bottle', label: 'Hel flaske' },
  { value: 'glass', label: 'Glass' },
  { value: 'half-bottle', label: 'Halvflaske' },
  { value: 'magnum', label: 'Magnum' },
  { value: 'decanter', label: 'Karaffel' },
  { value: 'tap', label: 'Tappet' },
  { value: 'can', label: 'Boks' },
];

const VISIBILITY_OPTIONS: { value: CheckInVisibility; label: string; description: string }[] = [
  { value: 'private', label: 'Privat', description: 'Bare du' },
  { value: 'household', label: 'Husholdning', description: 'Synlig for medlemmer i din kjeller' },
  { value: 'friends', label: 'Venner', description: 'Når venner er aktivert' },
  { value: 'public', label: 'Offentlig', description: 'Synlig for alle med lenken' },
];

export function CheckInModal({ open, onClose, bottleId, productId, bottleName, imageUrl }: CheckInModalProps) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [servingStyle, setServingStyle] = useState<string>('glass');
  const [venueName, setVenueName] = useState('');
  const [withFood, setWithFood] = useState('');
  const [visibility, setVisibility] = useState<CheckInVisibility>('household');

  // Reset form whenever the modal reopens.
  useEffect(() => {
    if (open) {
      setRating(0);
      setComment('');
      setServingStyle('glass');
      setVenueName('');
      setWithFood('');
      setVisibility('household');
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      CheckIns.create({
        bottleId,
        productId,
        rating: rating > 0 ? rating : undefined,
        comment: comment.trim() || undefined,
        servingStyle,
        venueName: venueName.trim() || undefined,
        withFood: withFood.trim() || undefined,
        visibility,
    }),
    onSuccess: () => {
      push({ title: 'Smaking logget', description: bottleName, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['checkins'] });
      void qc.invalidateQueries({ queryKey: ['feed'] });
      onClose();
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke logge smaking', description: err.message, tone: 'danger' }),
  });

  return (
    <Modal open={open} onClose={onClose} title={`Logg smaking — ${bottleName}`} size="md">
      <div className="grid gap-5">
        {imageUrl && (
          <div className="relative grid h-24 place-items-center overflow-hidden rounded-md bg-ink-soft sm:h-28">
            <BottleImage src={imageUrl} alt={bottleName} iconSize={40} mode="lightbox" />
          </div>
        )}

        {/* Rating — decimal steps from 0 to 5 */}
        <div className="grid gap-2">
          <span className="text-xs uppercase tracking-widest text-mist">Vurdering</span>
          <DecimalRating value={rating} onChange={setRating} />
        </div>

        <Textarea
          label="Notat"
          rows={3}
          placeholder="Hva tenkte du? Hvordan var aromaen, smaken, finishen?"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <label className="text-xs uppercase tracking-widest text-mist">Servering</label>
            <select
              value={servingStyle}
              onChange={(e) => setServingStyle(e.target.value)}
              className="h-10 rounded-md border border-line bg-ink-soft px-3 text-sm text-parchment focus:border-candle focus:outline-none"
            >
              {SERVING_STYLES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <Input
            label="Sted (valgfritt)"
            placeholder="Hjemme, restaurant, ..."
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
          />
        </div>

        <Input
          label="Med mat (valgfritt)"
          placeholder="Lammelår, oksefilet, indrefilet, ..."
          value={withFood}
          onChange={(e) => setWithFood(e.target.value)}
        />

        {/* Visibility */}
        <div className="grid gap-2">
          <span className="text-xs uppercase tracking-widest text-mist">Synlighet</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {VISIBILITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setVisibility(opt.value)}
                className={`grid gap-0.5 rounded-md border p-3 text-left transition-colors ${
                  visibility === opt.value
                    ? 'border-candle bg-candle/5'
                    : 'border-line hover:border-line-soft'
                }`}
              >
                <span className="text-sm text-parchment flex items-center gap-2">
                  <Icon name={iconForVisibility(opt.value)} size={12} />
                  {opt.label}
                </span>
                <span className="text-[10px] text-mist">{opt.description}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Avbryt</Button>
          <Button
            variant="primary"
            iconLeft="check-circle"
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Logg smaking
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function iconForVisibility(v: CheckInVisibility): 'lock' | 'users' | 'heart' | 'globe' {
  switch (v) {
    case 'private': return 'lock';
    case 'household': return 'users';
    case 'friends': return 'heart';
    case 'public': return 'globe';
  }
}

function DecimalRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const normalized = clampRating(value);
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {[1, 2, 3, 4, 5].map((star) => {
          const fillPct = Math.max(0, Math.min(1, normalized - (star - 1))) * 100;
          return (
            <button
              key={star}
              type="button"
              onClick={() => onChange(normalized === star ? 0 : star)}
              aria-label={`${star} stjerner`}
              className="relative inline-grid h-7 w-7 transition-transform hover:scale-110"
            >
              <Icon name="star" size={28} className="absolute inset-0 text-mist/30" />
              {fillPct > 0 && (
                <span aria-hidden className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                  <Icon
                    name="star"
                    size={28}
                    className="text-candle drop-shadow-[0_0_8px_rgba(230,180,107,0.4)]"
                  />
                </span>
              )}
            </button>
          );
        })}
        <label className="ml-1 inline-flex items-center gap-2 text-sm text-mist">
          <input
            type="number"
            min={0}
            max={5}
            step={0.1}
            inputMode="decimal"
            value={normalized}
            onChange={(e) => onChange(clampRating(Number(e.target.value)))}
            className="h-9 w-20 rounded-md border border-line bg-ink-soft px-2 text-sm text-parchment focus:border-candle focus:outline-none"
            aria-label="Vurdering fra 0 til 5"
          />
          / 5
        </label>
      </div>
      <input
        type="range"
        min={0}
        max={5}
        step={0.1}
        value={normalized}
        onChange={(e) => onChange(clampRating(Number(e.target.value)))}
        className="w-full accent-[var(--ui-color-candle)]"
        aria-label="Juster vurdering"
      />
    </div>
  );
}

function clampRating(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value * 10) / 10));
}
